import { trackEvent } from '../lib/analytics';
import { useState, useEffect } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState<boolean>(false);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);

  useEffect(() => {
    // Check if app is already running in standalone PWA mode
    const isStandaloneMode = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true ||
      document.referrer.includes('android-app://');
    
    setIsStandalone(isStandaloneMode);

    // Initial check from global window property captured in index.html
    if ((window as any).deferredInstallPrompt) {
      setDeferredPrompt((window as any).deferredInstallPrompt);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).deferredInstallPrompt = e;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      (window as any).deferredInstallPrompt = null;
      setDeferredPrompt(null);
      setIsInstalled(true);
      trackEvent('pwa_installed', 'engagement', 'native_appinstalled_event');
    };

    const handleCustomInstallable = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setDeferredPrompt(customEvent.detail as BeforeInstallPromptEvent);
      } else if ((window as any).deferredInstallPrompt) {
        setDeferredPrompt((window as any).deferredInstallPrompt);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-installable', handleCustomInstallable);
    window.addEventListener('pwa-installed', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-installable', handleCustomInstallable);
      window.removeEventListener('pwa-installed', handleAppInstalled);
    };
  }, []);

  const triggerInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    const promptEvent = deferredPrompt || (window as any).deferredInstallPrompt;
    if (!promptEvent) {
      return 'unavailable';
    }

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice.outcome === 'accepted') {
        (window as any).deferredInstallPrompt = null;
        setDeferredPrompt(null);
        setIsInstalled(true);
        trackEvent('pwa_installed', 'engagement', 'prompt_accepted');
        return 'accepted';
      }
      trackEvent('pwa_install_dismissed', 'engagement', 'prompt_dismissed');
      return 'dismissed';
    } catch (err) {
      console.warn('PWA install prompt error:', err);
      return 'unavailable';
    }
  };

  return {
    deferredPrompt: deferredPrompt || (window as any).deferredInstallPrompt,
    isInstallable: !!(deferredPrompt || (window as any).deferredInstallPrompt),
    isInstalled,
    isStandalone,
    triggerInstall,
  };
}
